/**
 * Where a web order stands, in the words My orders uses (PT-06, V5, docs/15).
 *
 * "Confirmed" comes with the nursery's order number, which is what a customer
 * quotes on the phone. But confirming makes a sales order, and the nursery can
 * still cancel that. A confirmed web order whose sales order was cancelled is
 * not coming, so it says "Cancelled by the nursery" rather than "Confirmed",
 * and every line reads "Not supplied" - whatever confirmed figure the server
 * sent beside it, since a cancelled order supplies nothing.
 */

import {
  compareQuantity,
  formatQuantity,
  isZeroQuantity,
  parseQuantity,
} from '@smf/core';

import type { WebOrder, WebOrderLine } from './portal-api.js';

/** Confirmed, and the order it became was cancelled since. */
function cancelledByNursery(order: WebOrder): boolean {
  return order.status === 'confirmed' && order.orderStatus === 'cancelled';
}

export function standing(order: WebOrder): string {
  switch (order.status) {
    case 'received':
      return 'Waiting for the nursery';
    case 'confirmed':
      if (cancelledByNursery(order)) return 'Cancelled by the nursery';
      return order.orderNumber === undefined
        ? 'Confirmed'
        : `Confirmed as order ${order.orderNumber}`;
    case 'declined':
      return 'Declined';
    case 'cancelled':
      return 'Cancelled';
  }
}

/** The pill's classes: the nursery's no in red, the customer's own cancel quiet. */
export function toneOf(order: WebOrder): string {
  switch (order.status) {
    case 'received':
      return 'pill pill--warn';
    case 'confirmed':
      return cancelledByNursery(order) ? 'pill pill--bad' : 'pill pill--good';
    case 'declined':
      return 'pill pill--bad';
    case 'cancelled':
      return 'pill';
  }
}

const NOT_SUPPLIED = { words: 'Not supplied', short: true } as const;

/** What the nursery confirmed of a line, once it has; otherwise nothing to say. */
export function confirmedOf(
  order: WebOrder,
  line: WebOrderLine,
): { readonly words: string; readonly short: boolean } | undefined {
  if (order.status !== 'confirmed') return undefined;
  if (cancelledByNursery(order)) return NOT_SUPPLIED;
  if (line.confirmedQuantity === undefined) return undefined;
  const confirmed = parseQuantity(line.confirmedQuantity);
  if (!confirmed.ok) return undefined;
  if (isZeroQuantity(confirmed.value)) return NOT_SUPPLIED;
  const asked = parseQuantity(line.quantity);
  return {
    words: `Confirmed ${formatQuantity(confirmed.value)}`,
    short: asked.ok && compareQuantity(confirmed.value, asked.value) !== 0,
  };
}
